'use client';

import { getCookie, setCookie } from 'cookies-next';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { observer } from 'mobx-react-lite';

import { Loading } from 'components/Loading';
import { TOPSORT_USER_ID } from 'constants/browserStorageNames';
import {
  getTopsortBanner,
  TopsortBannerData
} from 'services/topsort/getTopsortBanner';
import { sendTopsortEvent } from 'services/topsort/sendTopsortEvent';
import {
  AnalyticsEventName,
  analyticsEvents,
  transportMethods
} from 'lib/analytics';
import { browserLogger } from 'utils/logger/browserLogger';
import ArrowUpRight from 'assets/icons/ic-arrow-up-right.svg';
import { isMobile as getIsMobile } from 'utils/misc';
import locationStore from 'stores/LocationStore';
import { sendTrackingEvent } from 'lib/tracking/sendTrackingEvent';

import * as styles from './BannerTopsort.css';

function BannerTopsort({
  idBanner,
  marginBottom,
  pageTitleForTracking
}: {
  idBanner: string;
  marginBottom?: boolean;
  pageTitleForTracking?: string;
}) {
  const { ref, inView } = useInView({ triggerOnce: true });
  const [isLoading, setIsLoading] = useState(true);
  const [banner, setBanner] = useState<TopsortBannerData | null>(null);
  const topsortUserId = getCookie(TOPSORT_USER_ID);
  const location = locationStore.selectedLocation?.slug || undefined;

  const isInternalLink = useMemo(() => {
    if (!banner?.url) return false;
    try {
      const hostname = new URL(banner.url).hostname;
      return (
        hostname === 'sympla.com.br' || hostname.endsWith('.sympla.com.br')
      );
    } catch {
      return false;
    }
  }, [banner?.url]);

  const fetchBanner = async (isMobile: boolean) => {
    try {
      const bannerData = await getTopsortBanner(idBanner, isMobile, location);
      setBanner(bannerData || null);
    } catch (error) {
      browserLogger().error({
        message: 'Topsort Auction error',
        data: String(error)
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendEventAnalytics = useCallback(
    (eventName: AnalyticsEventName) => {
      const match = idBanner.match(/_(\d+)$/);
      const position = match ? Number(match[1]) : 1;
      sendTrackingEvent(eventName, {
        item_ads_position: position,
        item_ads_page_title: pageTitleForTracking || idBanner,
        item_ads_sponsor_id: banner?.vendorId,
        item_ads_campaign_id: banner?.id,
        transport: transportMethods.sendBeacon
      });
    },
    [banner, pageTitleForTracking, idBanner]
  );

  useEffect(() => {
    const isMobile = getIsMobile();
    fetchBanner(isMobile);
  }, [idBanner, topsortUserId, location]);

  useEffect(() => {
    if (!isLoading && inView && banner) {
      sendEventAnalytics(analyticsEvents.ads_viewed);
      sendTopsortEvent({
        type: 'impression',
        resolvedBidId: banner.resolvedBidId,
        idBanner
      });
    }
  }, [isLoading, inView, banner, sendEventAnalytics, idBanner]);

  const handleBannerClick = useCallback(() => {
    if (!banner) return;

    sendEventAnalytics(analyticsEvents.ads_clicked);

    if (banner.id) {
      const isProduction = process.env.NODE_ENV === 'production';

      setCookie('topsort_campaign_id', banner.id, {
        ...(isProduction ? { domain: '.sympla.com.br' } : {}),
        path: '/'
      });
    }

    let destinationPath = '';
    let eventIdMatch: RegExpMatchArray | null = null;

    try {
      const urlObj = new URL(banner.url);
      destinationPath = urlObj.pathname;
      eventIdMatch = destinationPath.match(
        /\/(?:evento\/(?:[^\/]+\/)?|event\/)(\d+)/
      );
    } catch (error) {
      browserLogger().error({
        message: 'Topsort Banner URL parsing error',
        data: String(error)
      });
    }

    if (banner.resolvedBidId && destinationPath && !eventIdMatch) {
      const adsContext = {
        bidId: banner.resolvedBidId,
        landingPath: destinationPath
      };

      const isProduction = process.env.NODE_ENV === 'production';
      const oneHoursInSeconds = 60 * 60;

      setCookie('ts_ads_context', JSON.stringify(adsContext), {
        maxAge: oneHoursInSeconds,
        ...(isProduction ? { domain: '.sympla.com.br' } : {}),
        path: '/'
      });
    }

    sendTopsortEvent({
      type: 'click',
      resolvedBidId: banner.resolvedBidId,
      idBanner
    });

    if (eventIdMatch) {
      sendTopsortEvent({
        type: 'click',
        resolvedBidId: banner.resolvedBidId,
        idBanner,
        additionalAttribution: {
          id: eventIdMatch[1],
          type: 'product'
        }
      });
    }
  }, [banner, sendEventAnalytics, idBanner]);

  if (isLoading) return <Loading />;

  if (!banner) return null;

  return (
    <div
      className={`${styles.ContainerBase} ${
        marginBottom ? styles.MarginBottom : ''
      }`}
      data-testid={`topsort-banner-${idBanner}`}
      ref={ref}
    >
      <a
        href={banner.url}
        target={isInternalLink ? '_self' : '_blank'}
        rel={isInternalLink ? undefined : 'noopener noreferrer'}
        className={styles.TopsortLink}
        onClick={handleBannerClick}
      >
        <img
          data-testid={`topsort-banner-img-${idBanner}`}
          src={banner.imageUrl}
          alt={`Banner ADS ${idBanner}`}
          className={styles.TopsortImg}
        />

        <div className={styles.topsortTag}>
          <span>Publicidade</span>
          <ArrowUpRight
            width={12}
            height={12}
            className={styles.topsortTagIcon}
          />
        </div>
      </a>
    </div>
  );
}

export default observer(BannerTopsort);
